import { logger } from '@inboundx/shared/platform';
import type { Session, User } from '@supabase/supabase-js';
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { supabase } from '@/lib/supabase';

interface AuthContextValue {
    user: User | null;
    session: Session | null;
    /** Stable across token refreshes; replaced after logout or a user change. */
    authenticatedSessionId: number | null;
    loading: boolean;
    signInWithGoogle: () => Promise<unknown>;
    sendEmailOtp: (email: string, captchaToken?: string) => Promise<unknown>;
    verifyEmailOtp: (email: string, token: string) => Promise<unknown>;
    signInWithPassword: (email: string, password: string) => Promise<unknown>;
    signUpWithPassword: (
        email: string,
        password: string,
        captchaToken?: string
    ) => Promise<unknown>;
    signOut: () => Promise<void>;
}

let nextAuthenticatedSessionId = 0;

const AuthContext = createContext<AuthContextValue | null>(null);

function getRedirectUrl(): string {
    const envRedirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL;
    if (envRedirectUrl) {
        return envRedirectUrl;
    }
    return `${window.location.origin}/auth/callback`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [authenticatedSessionId, setAuthenticatedSessionId] = useState<number | null>(null);
    const authenticatedUserId = useRef<string | null>(null);

    useEffect(() => {
        // ponytail: dev-only auto-login so `just dev` boots authenticated against the
        // local/branch Supabase. `import.meta.env.DEV` is false in every prod build, so
        // the creds and this branch are dead-code-eliminated and never bundled for prod.
        // MODE is 'test' under vitest. Without that guard a developer who has run
        // `just seed-branch` leaks VITE_DEV_AUTO_LOGIN_* from .env.local into the
        // unit tests, which then try to authenticate against a mock.
        const autoLoginEmail =
            import.meta.env.DEV && import.meta.env.MODE !== 'test'
                ? import.meta.env.VITE_DEV_AUTO_LOGIN_EMAIL
                : undefined;

        const devAutoLogin = async () => {
            const { error } = await supabase.auth.signInWithPassword({
                email: autoLoginEmail as string,
                password: import.meta.env.VITE_DEV_AUTO_LOGIN_PASSWORD ?? '',
            });
            if (error) {
                logger.error('Dev auto-login failed:', error);
            }
        };

        // While the stored session is still being validated below, supabase-js
        // emits INITIAL_SESSION with that stored session. Publishing it would let
        // AuthGuard's profile query fire on a token we already suspect, which
        // fails with PGRST301 and drops the developer on the onboarding wizard.
        let validated = !autoLoginEmail;

        const publish = (next: Session | null) => {
            const nextUserId = next?.user.id ?? null;
            if (nextUserId !== authenticatedUserId.current) {
                authenticatedUserId.current = nextUserId;
                setAuthenticatedSessionId(nextUserId ? ++nextAuthenticatedSessionId : null);
            }
            setSession(next);
            setUser(next?.user ?? null);
        };

        supabase.auth.getSession().then(async ({ data: { session } }) => {
            if (!autoLoginEmail) {
                publish(session);
                setLoading(false);
                return;
            }

            // Whatever happens below, the app must stop showing its loading state;
            // an exception here would otherwise hang the boot screen for good.
            try {
                // getSession only reads storage; it does not check the token is
                // still good. A stored session the server rejects — after
                // repointing a worktree at another Supabase project, or once a
                // branch database is deleted — would otherwise block auto-login
                // forever, leaving a login screen no credential in .env.local can
                // get past.
                if (!session) {
                    await devAutoLogin();
                    return;
                }

                const { error } = await supabase.auth.getUser();
                if (error) {
                    // Deliberately do NOT publish this session first. Anything
                    // reading the context — AuthGuard's accessible-domains query
                    // above all — would fire with the dead token and land the user
                    // on the onboarding wizard before the replacement arrives.
                    // signInWithPassword replaces the stored session and
                    // onAuthStateChange publishes the good one.
                    logger.warn('Dev session is no longer valid; signing in again.', error);
                    await devAutoLogin();
                    return;
                }

                publish(session);
            } catch (err) {
                logger.error('Dev auto-login could not run:', err);
            } finally {
                validated = true;
                setLoading(false);
            }
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            if (!validated && event === 'INITIAL_SESSION') {
                return;
            }
            publish(session);
            setLoading(false);
        });

        return () => subscription.unsubscribe();
    }, []);

    const signInWithGoogle = useCallback(async () => {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: getRedirectUrl(),
            },
        });

        if (error) {
            logger.error('Error signing in with Google:', error);
            throw error;
        }

        return data;
    }, []);

    const sendEmailOtp = useCallback(async (email: string, captchaToken?: string) => {
        const { data, error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                shouldCreateUser: true,
                // Keep this during the OTP rollout so previously issued magic links
                // still return to the existing callback page until they expire.
                emailRedirectTo: getRedirectUrl(),
                ...(captchaToken ? { captchaToken } : {}),
            },
        });

        if (error) {
            logger.error('Error sending email OTP:', error);
            throw error;
        }

        return data;
    }, []);

    const verifyEmailOtp = useCallback(async (email: string, token: string) => {
        const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'email',
        });

        if (error) {
            logger.error('Error verifying email OTP:', error);
            throw error;
        }

        return data;
    }, []);

    const signInWithPassword = useCallback(async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            logger.error('Error signing in with password:', error);
            throw error;
        }

        return data;
    }, []);

    // Staff-only password signup (IX-3950): lets @userose.ai testers create an
    // account without the email OTP round-trip. When email confirmations
    // are off (local/staging) this returns a session immediately and drops the
    // user straight into the onboarding funnel.
    const signUpWithPassword = useCallback(
        async (email: string, password: string, captchaToken?: string) => {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: getRedirectUrl(),
                    ...(captchaToken ? { captchaToken } : {}),
                },
            });

            if (error) {
                logger.error('Error signing up with password:', error);
                throw error;
            }

            return data;
        },
        []
    );

    const signOut = useCallback(async () => {
        const { error } = await supabase.auth.signOut();
        if (error) {
            logger.error('Error signing out:', error);
            throw error;
        }
    }, []);

    const value = useMemo(
        () => ({
            user,
            session,
            authenticatedSessionId,
            loading,
            signInWithGoogle,
            sendEmailOtp,
            verifyEmailOtp,
            signInWithPassword,
            signUpWithPassword,
            signOut,
        }),
        [
            user,
            session,
            authenticatedSessionId,
            loading,
            signInWithGoogle,
            sendEmailOtp,
            verifyEmailOtp,
            signInWithPassword,
            signUpWithPassword,
            signOut,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
